import clsx from 'clsx'
import type { RefObject } from 'react'
import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import type { CalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import FlexibleDateSearchField from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import type { TripStop } from 'tripPlanner/api/types'

import TripContextSearchDateDropdown from './TripContextSearchDateDropdown'

interface Props {
  dropdownAnchorRef: RefObject<HTMLElement | null>
  dropdownPortaled?: boolean
  onDateToggle: (e: React.MouseEvent<HTMLButtonElement>) => void
  onDatesApply: (shouldPerformSearch?: boolean) => void
  onDatesDecline: () => void
  isToggled: boolean
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  durationOptionsCustom?: Array<DurationOption>
  isSpecificSearchEnabled?: boolean
  className?: string
  showFlexibleYearSelector?: boolean
  closeMenu?: () => void
  showAIGlow?: boolean
  disableFlexible?: boolean
  /** Called when the traveller changes dates themselves. Omit dates for a check-in-only interaction. */
  onUserDatesChanged?: (dates?: App.TypeaheadExtractedTravelDates) => void
  /** Month the calendar opens on before a check-in is picked. Viewport only. */
  focusMonth?: CalendarFocusMonth
  /** The trip, drawn behind the dates as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this search belongs to, so its bar can hold the foreground. */
  contextStopId?: number
}

/**
 * The desktop check-in / check-out fields, with a dropdown that shows the
 * rest of the trip behind the calendar.
 *
 * A drop-in for `SearchDateInputDesktop` wherever the trip planner already
 * owns the composition — the fields, the state and the apply flow are
 * identical, and only the calendar inside the dropdown differs.
 */
function TripContextSearchDateInput(props: Props) {
  const {
    dropdownAnchorRef,
    dropdownPortaled,
    onDateToggle,
    isToggled,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    durationOptionsCustom,
    isSpecificSearchEnabled = true,
    className,
    showFlexibleYearSelector,
    closeMenu,
    showAIGlow = false,
    disableFlexible,
    onUserDatesChanged,
    focusMonth,
    tripStops,
    contextStopId,
  } = props

  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } =
    useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const intl = useIntl()

  const toggleDates = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!isToggled) onDateToggle(e)
  }, [isToggled, onDateToggle])

  /**
   * Clears a half-finished range once the dropdown closes, and restores the
   * flexible mode when one is pending, so the fields and the calendar tell the
   * same story next time it opens.
   */
  useEffect(() => {
    if (isToggled) return

    if (!checkinDate) {
      globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }
    if (flexibleMonths && flexibleNights) {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
        optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled])

  const checkInFieldRef = useRef<HTMLButtonElement>(null)
  const checkOutFieldRef = useRef<HTMLButtonElement>(null)
  const checkInAndOutFieldsRefs = useMemo(() => [checkInFieldRef, checkOutFieldRef], [])

  const anytimePlaceholder = intl.formatMessage({
    id: 'RigImioe5lWt2RsFEZL9',
    defaultMessage: 'Anytime',
    description: 'Placeholder for date field indicating any date is acceptable',
  })

  return <>
    {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME && <SearchFormField
      label={intl.formatMessage({
        id: 'IRYpNHhIsJ-g0WsJ7hUR',
        defaultMessage: 'When?',
        description: 'Form field label for the date field when anytime is selected',
      })}
      placeholder={anytimePlaceholder}
      onClick={onDateToggle}
    />}
    {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && <SearchFormFieldGroup>
      <SearchFormField
        ref={checkInFieldRef}
        className={clsx({ 'focus-within': isToggled && !checkoutDate && !checkinDate })}
        label={intl.formatMessage({
          id: 'BZImHfWDmEabHe95A4Ie',
          defaultMessage: 'Check in',
          description: 'Form field label for the hotel check-in date in the booking date picker',
        })}
        onClick={toggleDates}
        placeholder={anytimePlaceholder}
        value={checkinDate ? intlDateFormatter(checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
        showAIGlow={showAIGlow}
      />
      <SearchFormField
        ref={checkOutFieldRef}
        className={clsx({ 'focus-within': isToggled && checkinDate && !checkoutDate })}
        label={intl.formatMessage({
          id: 'zNj1m3NcaWN8Inz8iuy9',
          defaultMessage: 'Check out',
          description: 'Label for check-out date field in the date selection or calendar picker',
        })}
        onClick={toggleDates}
        placeholder={anytimePlaceholder}
        value={checkoutDate ? intlDateFormatter(checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
        showAIGlow={showAIGlow}
      />
    </SearchFormFieldGroup>}
    {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && <FlexibleDateSearchField
      className={className}
      onDateToggle={onDateToggle}
      flexibleMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
      flexibleNightsId={flexibleNights}
      durationOptionsCustom={durationOptionsCustom}
      showAIGlow={showAIGlow}
    />}
    <TripContextSearchDateDropdown
      anchorRef={dropdownAnchorRef}
      portaled={dropdownPortaled}
      triggerRef={checkInAndOutFieldsRefs}
      open={isToggled}
      onDatesApply={onDatesApply}
      onDatesDecline={onDatesDecline}
      anytimeDateSelected={anytimeDateSelected}
      selectAnytimeDate={selectAnytimeDate}
      selectSpecificDates={selectSpecificDates}
      selectFlexibleDates={selectFlexibleDates}
      dateSearchOptionId={dateSearchOptionId}
      durationOptionsCustom={durationOptionsCustom}
      isSpecificSearchEnabled={isSpecificSearchEnabled}
      showFlexibleYearSelector={showFlexibleYearSelector}
      closeMenu={closeMenu}
      disableFlexible={disableFlexible}
      onUserDatesChanged={onUserDatesChanged}
      focusMonth={focusMonth}
      tripStops={tripStops}
      contextStopId={contextStopId}
    />
  </>
}

export default TripContextSearchDateInput
